import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import DataTable from '@common/components/datatable';
import { MutationCard } from './mutation-card';
import * as integrationFunctionsSelector from '../../selectors/integration-functions';
import * as integrationCommunitiesSelector from '../../selectors/integration-communities';
import { SyncResult } from './types';

interface SyncResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  syncData: SyncResult | null;
  onConfirmSync: () => void;
  isLoading?: boolean;
}

function parseSyncResponse(syncResponse: SyncResult | null, t: any) {
  if (!syncResponse) {
    return {
      userMutations: { new: 0, updated: 0, removed: 0 },
      communityMutations: { new: 0, updated: 0, removed: 0 },
      functionMutations: { new: 0, updated: 0, removed: 0 },
      validationErrors: [],
    };
  }

  const { users } = syncResponse.mutations;
  const { networks } = syncResponse.mutations;
  const { functions } = syncResponse.mutations;

  const userMutations = {
    new: users.invited?.length || 0,
    updated: users.updated?.length || 0,
    removed: users.removed?.length || 0,
  };
  const communityMutations = {
    new: networks.added?.length || 0,
    updated: 0,
    removed: networks.removed?.length || 0,
  };
  const functionMutations = {
    new: functions.added?.length || 0,
    updated: 0,
    removed: functions.removed?.length || 0,
  };

  const validationErrors = (users.validationErrors || []).map(
    (err: any, idx: number) => {
      return {
        id: idx,
        name: err.item
          ? `${err.item.firstName || ''} ${err.item.lastName || ''}`.trim()
          : t('organisation:sync_result_unknown_user'),
        community: err.item?.externalNetworkIds || [],
        function: err.item?.externalFunctionIds || [],
        errors: Array.isArray(err.validationErrors)
          ? err.validationErrors
          : [err.message || t('organisation:sync_result_unknown_error')],
      };
    }
  );

  const result = {
    userMutations,
    communityMutations,
    functionMutations,
    validationErrors,
  };

  return result;
}

export const SyncResultModal: React.FC<SyncResultModalProps> = ({
  isOpen,
  onClose,
  syncData,
  onConfirmSync,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const communitygroups = useSelector(
    integrationCommunitiesSelector.getIntegrationCommunities
  );
  const functiongroups = useSelector(
    integrationFunctionsSelector.getIntegrationFunctions
  );

  const {
    userMutations,
    communityMutations,
    functionMutations,
    validationErrors,
  } = parseSyncResponse(syncData, t);

  const transformedValidationErrors = useMemo(() => {
    if (!validationErrors || !communitygroups || !functiongroups) {
      return validationErrors;
    }

    return validationErrors.map((error: any) => ({
      ...error,
      community:
        error.community?.map((networkId: string) => {
          const communitygroup = communitygroups.find((group: any) => {
            if (Array.isArray(group.externalId)) {
              return group.externalId.includes(networkId);
            }
            return group.externalId === networkId;
          });
          return communitygroup?.name || networkId;
        }) || [],
      function:
        error.function?.map((functionId: string) => {
          const functiongroup = functiongroups.find((group: any) => {
            if (Array.isArray(group.externalId)) {
              return group.externalId.includes(functionId);
            }
            return group.externalId === functionId;
          });
          return functiongroup?.name || functionId;
        }) || [],
    }));
  }, [validationErrors, communitygroups, functiongroups]);

  return (
    <ModalWindow show={isOpen} onHide={onClose} size="large">
      <ModalContent
        title={t('organisation:sync_result_title')}
        onHide={onClose}
        confirmButtonText={t('organisation:sync_result_confirm')}
        onConfirm={onConfirmSync}
        confirmButtonLoading={isLoading}
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-mt-2 tw-mb-4 tw-rounded-lg tw-border tw-border-solid
          tw-border-gray-light-50 tw-bg-white tw-flex tw-flex-row"
          >
            <MutationCard
              title={t('organisation:sync_result_user_mutations')}
              mutations={userMutations}
            />

            <div className="tw-w-px tw-border tw-border-solid tw-border-gray-light-50" />
            <MutationCard
              title={t('organisation:sync_result_community_mutations')}
              mutations={communityMutations}
            />

            <div className="tw-w-px tw-border tw-border-solid tw-border-gray-light-50" />
            <MutationCard
              title={t('organisation:sync_result_function_mutations')}
              mutations={functionMutations}
            />
          </div>

          <div className="tw-h-[1px] tw-bg-gray-neutral-50 tw-flex-grow " />
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-font-inter tw-font-medium tw-text-lg tw-text-gray-900">
              {t('organisation:sync_result_validation_errors')}
            </div>
            <div className="tw-font-inter tw-font-medium tw-text-base tw-text-gray-muted">
              {t('organisation:sync_result_validation_errors_description')}
            </div>
          </div>

          <DataTable
            columns={[
              { key: 'name', label: t('organisation:sync_result_name') },
              {
                key: 'community',
                label: t('organisation:sync_result_community'),
                options: {
                  render: (value: any) => {
                    if (!value || !Array.isArray(value.community))
                      return <span>-</span>;
                    return value.community.map((name: any) => (
                      <span
                        className="tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-mr-1 tw-text-xs"
                      >
                        {name}
                      </span>
                    ));
                  },
                },
              },
              {
                key: 'function',
                label: t('organisation:sync_result_function'),
                options: {
                  render: (value: any) => {
                    if (!value || !Array.isArray(value.function))
                      return <span>-</span>;
                    return value.function.map((name: any) => (
                      <span
                        className="tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-mr-1 tw-text-xs"
                      >
                        {name}
                      </span>
                    ));
                  },
                },
              },
              {
                key: 'errors',
                label: t('organisation:sync_result_errors'),
                options: {
                  render: (value: any) => {
                    if (!value || !Array.isArray(value.errors))
                      return <span>-</span>;
                    return value.errors.map((err: any) => (
                      <div className="tw-text-xs tw-text-red-500">
                        {err}
                      </div>
                    ));
                  },
                },
              },
            ]}
            data={transformedValidationErrors}
            emptyView={
              <p className="tw-text-gray-muted tw-text-center tw-mt-6 tw-mb-6">
                {t('organisation:sync_result_no_validation_errors')}
              </p>
            }
            options={{ dense: true }}
          />
        </div>
      </ModalContent>
    </ModalWindow>
  );
};
